<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import type { Excursion } from "@/components/RegistrationProcess/types/events.ts";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import ExcursionOptionalItem from "../ExcursionOptionalItem/index.vue";
import type { Props } from "./types.ts";

defineOptions({
  name: "ExcursionOptionalSection",
});

const modelValue = defineModel<Excursion[]>({ required: true });
const props = withDefaults(defineProps<Props>(), {
  title: "Enhance Your Experience",
  useBoldTitle: false,
});
const emit = defineEmits<(event: "optional-item-fully-visible") => void>();

// Branded storefronts get a gray accent band with navy text; see ExcursionIncludedSection.
const { secondaryColor } = useTenantTheming();

const optionalItemElements = ref<Array<HTMLElement | null>>([]);
const hasEmittedVisibleEvent = ref(false);

let visibilityObserver: IntersectionObserver | null = null;

const setOptionalItemRef = (element: unknown, index: number) => {
  optionalItemElements.value[index] = element instanceof HTMLElement ? element : null;
};

const disconnectObserver = () => {
  visibilityObserver?.disconnect();
  visibilityObserver = null;
};

const initializeObserver = () => {
  if (hasEmittedVisibleEvent.value || typeof IntersectionObserver === "undefined") {
    return;
  }

  disconnectObserver();
  visibilityObserver = new IntersectionObserver(
    (entries) => {
      const hasFullyVisibleItem = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 1);
      if (!hasFullyVisibleItem || hasEmittedVisibleEvent.value) {
        return;
      }

      hasEmittedVisibleEvent.value = true;
      emit("optional-item-fully-visible");
      disconnectObserver();
    },
    { threshold: [1] },
  );

  optionalItemElements.value.forEach((element) => {
    if (element) {
      visibilityObserver?.observe(element);
    }
  });
};

watch(
  () => modelValue.value.length,
  async () => {
    optionalItemElements.value = [];
    await nextTick();
    initializeObserver();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  disconnectObserver();
});
</script>

<template>
  <div class="flex flex-col w-full gap-4 font-montserrat" data-testid="excursion-optional-section">
    <div class="flex flex-col w-full gap-1 justify-start items-start">
      <div class="flex w-full" :class="useBoldTitle ? ['py-2 px-4', secondaryColor ? 'bg-gray-275' : 'bg-blue-400'] : ''">
        <span class="font-bold text-university-primary" :class="useBoldTitle ? 'text-xl' : 'text-lg'" data-testid="excursion-optional-section-title">
          {{ title }}
        </span>
      </div>
      <div>
        <span class="text-university-primary" data-testid="excursion-optional-section-description">Optional curated experiences to explore beyond your city, available for an additional fee.</span>
      </div>
    </div>
    <div class="flex flex-col gap-4">
      <div v-for="(excursion, index) in modelValue" :key="index" data-testid="excursion-optional-item-wrapper">
        <div :ref="(element) => setOptionalItemRef(element, index)">
          <ExcursionOptionalItem :model-value="excursion" />
        </div>
      </div>
    </div>
  </div>
</template>

